/**
 * Sentry wrapper — single place that imports @sentry/react.
 *
 * No DSN in .env → Sentry.init is skipped → all exports are silent no-ops.
 * This means the app runs identically with or without Sentry, and the DSN
 * itself is the feature flag (rollback = `VITE_SENTRY_DSN=""` + rebuild).
 */
import * as Sentry from '@sentry/react';

type AnyError = Error | unknown;

let _initialized = false;

export function initSentry(): void {
  if (_initialized) return;
  _initialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const environment =
    (import.meta.env.VITE_SENTRY_ENV as string | undefined) ??
    (import.meta.env.MODE === 'production' ? 'production' : 'staging');

  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      ignoreErrors: [
        'Minified React error #418',
        'ResizeObserver loop',
        'Non-Error promise rejection captured',
      ],
      beforeSend(event) {
        if (typeof window !== 'undefined') {
          const { hostname } = window.location;
          if (hostname === 'localhost' || hostname === '127.0.0.1') return null;
        }
        // Drop CSP unsafe-eval errors whose stack is anonymous-only —
        // these come from browser extensions injecting scripts (caught
        // by window.onerror), not from our code or vendored libs.
        const ex = event.exception?.values?.[0];
        if (
          ex?.type === 'EvalError' &&
          typeof ex.value === 'string' &&
          ex.value.includes('unsafe-eval')
        ) {
          const frames = ex.stacktrace?.frames ?? [];
          const allAnon =
            frames.length > 0 &&
            frames.every(f => !f.filename || f.filename === '<anonymous>');
          if (allAnon) return null;
        }
        return event;
      },
    });
  } catch (err) {
    console.warn('[sentry] init failed, continuing without monitoring', err);
  }
}

export interface CaptureContext {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

export function captureException(error: AnyError, context?: CaptureContext): void {
  if (!_initialized || !import.meta.env.VITE_SENTRY_DSN) return;
  try {
    Sentry.captureException(error, context);
  } catch {
    /* never let Sentry kill the app */
  }
}

export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!_initialized || !import.meta.env.VITE_SENTRY_DSN) return;
  try {
    Sentry.captureMessage(message, level);
  } catch {
    /* swallow */
  }
}

export function addBreadcrumb(crumb: { category: string; message: string; data?: Record<string, unknown> }): void {
  if (!_initialized || !import.meta.env.VITE_SENTRY_DSN) return;
  try {
    Sentry.addBreadcrumb({ level: 'info', ...crumb });
  } catch {
    /* swallow */
  }
}

export function setUser(user: { id?: string; email?: string; username?: string } | null): void {
  if (!_initialized || !import.meta.env.VITE_SENTRY_DSN) return;
  try {
    Sentry.setUser(user);
  } catch {
    /* swallow */
  }
}
